import { FC, memo, useEffect, useRef } from 'react'
import { useYMaps } from '@pbe/react-yandex-maps'
import { DEFAULT_CENTER, MapProps } from 'components/Map'
import { useIsMobile } from 'hooks/media'

type BoundChange = { address: string; coords: CoordinateType }
type SearchMapProps = MapProps & {
  onBoundsChange?: (data: BoundChange) => void
}

const SearchMap: FC<SearchMapProps> = (props) => {
  const { height, width, center, zoom, onBoundsChange } = props
  const isMobile = useIsMobile()
  const mapRef = useRef(null)
  const ymaps = useYMaps([
    'Map',
    'Placemark',
    'control.GeolocationControl',
    'control.SearchControl',
    'control.ZoomControl',
    'geocode'
  ])

  useEffect(() => {
    if (!ymaps || !mapRef.current) {
      return
    }

    const map = new ymaps.Map(mapRef.current, {
      center: center || DEFAULT_CENTER,
      zoom,
      controls: ['geolocationControl', 'zoomControl']
    })
    const placeMark = new ymaps.Placemark(map.getCenter(), {}, { draggable: true })
    const searchControl = new ymaps.control.SearchControl({
      options: {
        provider: 'yandex#search',
        noPlacemark: true,
        noPopup: true,
        noSuggestPanel: true
      }
    })
    const handleBoundsChange = async (newCenter: CoordinateType) => {
      const results = await ymaps.geocode(newCenter)
      const firstGeoObject = results.geoObjects.get(0) as ymaps.GeocodeResult
      const address = firstGeoObject.getAddressLine()

      onBoundsChange?.({
        coords: newCenter,
        address
      })
    }

    placeMark.events.add('dragend', async (event) => {
      const target = event.get('target') as unknown as ymaps.Placemark
      const newCenter = target?.geometry?.getCoordinates() as CoordinateType

      if (newCenter) {
        await map.setCenter(newCenter)
        await handleBoundsChange(newCenter)
      }
    })

    map.events.add('boundschange', async (event) => {
      const newCenter = event.get('newCenter')

      await handleBoundsChange(newCenter)
      placeMark.geometry?.setCoordinates(newCenter)
    })

    map.controls.add(searchControl)
    map.geoObjects.add(placeMark)

    return () => {
      mapRef.current = null
    }
  }, [center, onBoundsChange, ymaps, zoom])

  return <div ref={mapRef} style={{ flexGrow: '1', width: width || '100%', height: isMobile ? '100%' : height }} />
}

export default memo(SearchMap)
